'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { locales } from '@/shared/i18n/index';
import { updatePreferredLocale } from '@/contexts/family';

type Locale = (typeof locales)[number];

const localeFlags: Record<Locale, string> = {
  ru: '🇷🇺',
  en: '🇺🇸',
  cs: '🇨🇿',
  uk: '🇺🇦',
  de: '🇩🇪',
  fr: '🇫🇷',
};

interface LanguageSelectorProps {
  variant?: 'select' | 'native';
}

/**
 * Language selector component
 * - select: Desktop version using shadcn Select
 * - native: Mobile version using native HTML select
 */
export function LanguageSelector({ variant = 'select' }: LanguageSelectorProps) {
  const t = useTranslations('common');
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const [isPending, setIsPending] = useState(false);
  const { data: session, update } = useSession();

  const handleLanguageChange = async (newLocale: string) => {
    setIsPending(true);
    // Save locale to account if authenticated, then refresh JWT
    if (session?.user) {
      try {
        await updatePreferredLocale(newLocale);
        await update(); // Refresh JWT so preferredLocale claim is up-to-date
      } catch {
        // Continue with redirect even if session update fails
      }
    }

    const currentLocaleMatch = pathname.match(/^\/([a-z]{2})(\/.*)?$/);

    let newPathname;
    if (currentLocaleMatch) {
      const [, , restOfPath] = currentLocaleMatch;
      newPathname = `/${newLocale}${restOfPath || ''}`;
    } else {
      newPathname = `/${newLocale}${pathname === '/' ? '' : pathname}`;
    }

    window.location.href = newPathname;
  };

  if (variant === 'native') {
    return (
      <select
        data-testid="language-selector-native"
        value={locale}
        onChange={(e) => handleLanguageChange(e.target.value)}
        disabled={isPending}
        className="h-8 landscape:h-6 px-2 landscape:px-1 text-sm landscape:text-xs rounded-lg border-2 border-border bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
      >
        {locales.map((lang) => (
          <option key={lang} value={lang}>
            {localeFlags[lang]}
          </option>
        ))}
      </select>
    );
  }

  return (
    <Select value={locale} onValueChange={handleLanguageChange} disabled={isPending}>
      <SelectTrigger
        data-testid="language-selector"
        className="w-20 h-11 cursor-pointer hover:bg-primary/20 transition-colors rounded-full border-2 border-border bg-primary/10"
        aria-label={t('selectLanguage')}
      >
        <SelectValue>{localeFlags[locale]}</SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-background/95 backdrop-blur-sm border-border shadow-xl rounded-2xl">
        {locales.map((lang) => (
          <SelectItem
            key={lang}
            value={lang}
            className="cursor-pointer hover:bg-primary/10 rounded-xl transition-colors"
          >
            {localeFlags[lang]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
